import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FiX } from 'react-icons/fi'

const ImageLightboxContext = createContext(null)

function looksLikeVideoUrl(url) {
  if (!url || typeof url !== 'string') return false
  const p = url.split('?')[0].toLowerCase()
  return /\.(mp4|webm|mov|m4v|ogv)(\?|$)/i.test(p)
}

function LightboxVideo({ src }) {
  const videoRef = useRef(null)
  const [status, setStatus] = useState('loading')

  const markUnsupported = useCallback(() => {
    const el = videoRef.current
    if (el) {
      el.pause()
      el.removeAttribute('src')
      el.load()
    }
    setStatus('unsupported')
  }, [])

  const onLoadedMetadata = (e) => {
    const el = e.currentTarget
    if (!el.videoWidth) {
      markUnsupported()
      return
    }
    setStatus('ready')
    const playAttempt = el.play()
    if (playAttempt && typeof playAttempt.catch === 'function') {
      playAttempt.catch(() => {
        /* NotAllowedError: keep the video visible with controls for a manual play. */
      })
    }
  }

  if (status === 'unsupported') {
    return (
      <div
        className="w-[min(92vw,420px)] rounded-lg bg-[#12182c] px-5 py-6 text-left shadow-2xl ring-1 ring-white/15"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-white">This video cannot be displayed</h3>
        <p className="mt-2 text-sm leading-relaxed text-gray-300">
          This video&apos;s image track cannot be decoded in this browser. Download the original and
          re-upload as H.264 MP4.
        </p>
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-block text-sm font-medium text-cyan-400 hover:text-cyan-300 hover:underline"
        >
          Download original
        </a>
      </div>
    )
  }

  return (
    <video
      ref={videoRef}
      key={src}
      src={src}
      controls={status === 'ready'}
      playsInline
      preload="metadata"
      className="max-h-[min(88vh,880px)] max-w-full rounded-lg shadow-2xl ring-1 ring-white/15"
      onLoadedMetadata={onLoadedMetadata}
      onError={markUnsupported}
      onClick={(e) => e.stopPropagation()}
    />
  )
}

export function ImageLightboxProvider({ children }) {
  const [item, setItem] = useState(null)

  const open = useCallback((src, alt = '', options) => {
    if (!src || typeof src !== 'string') return
    const explicit = options?.type
    const type =
      explicit === 'video' || explicit === 'image'
        ? explicit
        : looksLikeVideoUrl(src)
          ? 'video'
          : 'image'
    setItem({ src, alt: alt || '', type })
  }, [])

  const close = useCallback(() => setItem(null), [])

  useEffect(() => {
    if (!item) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [item, close])

  return (
    <ImageLightboxContext.Provider value={{ open, close }}>
      {children}
      {item &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[240] flex items-center justify-center p-4 sm:p-8"
            role="dialog"
            aria-modal="true"
            aria-label={item?.type === 'video' ? 'Video' : 'Image'}
          >
            <button
              type="button"
              className="absolute inset-0 bg-black/88 backdrop-blur-sm"
              onClick={close}
              aria-label="Close"
            />
            <div className="relative z-[1] flex max-h-[min(92vh,920px)] max-w-[min(96vw,1200px)] flex-col items-center">
              <button
                type="button"
                onClick={close}
                className="absolute -top-1 right-0 z-10 rounded-full bg-white/12 p-2 text-white transition-colors hover:bg-white/20 sm:-right-2 sm:-top-2"
                aria-label="Close"
              >
                <FiX className="h-6 w-6" />
              </button>
              {item?.type === 'video' ? (
                <LightboxVideo key={item.src} src={item.src} />
              ) : (
                <img
                  src={item.src}
                  alt={item.alt}
                  className="max-h-[min(88vh,880px)] max-w-full rounded-lg object-contain shadow-2xl ring-1 ring-white/15"
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </div>
          </div>,
          document.body
        )}
    </ImageLightboxContext.Provider>
  )
}

/** Returns null outside the provider — components can use without crashing */
export function useImageLightboxOptional() {
  return useContext(ImageLightboxContext)
}
